/**
 * Search - Handles search input with debounce and min length validation
 */

import type { SearchConfig } from './types';

interface InternalSearchConfig {
    inputSelector: string;
    clearSelector: string | null;
    debounceMs: number;
    minLength: number;
    onSearch: (query: string) => void;
}

export class Search {
    private readonly config: InternalSearchConfig;
    private readonly input: HTMLInputElement;
    private readonly clearButton: HTMLElement | null;
    private readonly inputWrapper: HTMLElement | null;

    private searchTimeout: number | null = null;
    private isSearching: boolean = false;

    constructor(config: SearchConfig) {
        this.config = {
            clearSelector: null,
            debounceMs: 500,
            minLength: 0,
            ...config
        };

        const input = document.querySelector<HTMLInputElement>(this.config.inputSelector);

        if (!input) {
            throw new Error('Search: Input element not found');
        }

        this.input = input;
        this.inputWrapper = input.closest('.input-group') || input.parentElement;

        this.clearButton = this.config.clearSelector
            ? document.querySelector<HTMLElement>(this.config.clearSelector)
            : null;

        this.bindEvents();
        this.initFromUrl();
    }

    private bindEvents(): void {
        // Prevent form submission if input is inside a form
        this.input.closest('form')?.addEventListener('submit', (e: Event) => {
            e.preventDefault();
            this.performSearch();
        });

        this.input.addEventListener('input', () => {
            this.debouncedSearch();
        });

        this.clearButton?.addEventListener('click', () => {
            this.clear();
        });
    }

    private initFromUrl(): void {
        const urlParams = new URLSearchParams(window.location.search);
        const searchParam = urlParams.get('search');

        if (searchParam) {
            this.input.value = searchParam;
        }

        this.updateClearButtonVisibility();
    }

    private debouncedSearch(): void {
        if (this.searchTimeout !== null) {
            clearTimeout(this.searchTimeout);
        }

        // Show searching indicator during debounce
        this.showSearching();

        this.searchTimeout = window.setTimeout(() => {
            this.performSearch();
        }, this.config.debounceMs);
    }

    private performSearch(): void {
        const query = this.getValue();

        if (query.length > 0 && query.length < this.config.minLength) {
            this.hideSearching();

            return;
        }

        this.updateClearButtonVisibility();
        this.config.onSearch(query);
        // Note: hideSearching should be called by the consumer after search completes
    }

    private showSearching(): void {
        this.isSearching = true;
        this.input.style.cursor = 'wait';
        this.inputWrapper?.classList.add('is-searching');
    }

    public hideSearching(): void {
        this.isSearching = false;
        this.input.style.cursor = '';
        this.inputWrapper?.classList.remove('is-searching');
    }

    public getIsSearching(): boolean {
        return this.isSearching;
    }

    private updateClearButtonVisibility(): void {
        if (!this.clearButton) {
            return;
        }

        const hasValue = this.input.value.trim().length > 0;
        this.clearButton.classList.toggle('d-none', !hasValue);
    }

    public getValue(): string {
        return this.input.value.trim();
    }

    public setValue(value: string): void {
        this.input.value = value;
        this.updateClearButtonVisibility();
    }

    public clear(): void {
        this.cancelPendingSearch();
        this.hideSearching();
        this.input.value = '';
        this.updateClearButtonVisibility();
        this.config.onSearch('');
    }

    public getMinLength(): number {
        return this.config.minLength;
    }

    public cancelPendingSearch(): void {
        if (this.searchTimeout !== null) {
            clearTimeout(this.searchTimeout);
            this.searchTimeout = null;
        }
    }
}

export default Search;